import React, { useState, useEffect } from 'react';
import { db, auth, storage } from '../../firebase';
import { 
    collection, addDoc, getDocs, updateDoc, deleteDoc, 
    doc, query, where, getDoc, serverTimestamp 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';

// MUI Components
import {
    Paper, Typography, TextField, Button, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Pagination, CircularProgress, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, Avatar, Tooltip
} from '@mui/material';
import {
    Delete as DeleteIcon, Edit as EditIcon, Search as SearchIcon,
    Add as AddIcon, Close as CloseIcon, CloudUpload as UploadIcon,
    CheckCircle as CheckCircleIcon, AccountBalance as BankIcon,
    Visibility as ViewIcon
} from '@mui/icons-material';

const VendorMgmt = () => {
    const [vendors, setVendors] = useState([]);
    const [schoolId, setSchoolId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);

    // Image Preview Popup State
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');

    const [formData, setFormData] = useState({
        name: '', companyName: '', mobile: '', gst: '', address: '',
        bankName: '', accountName: '', accountNo: '', ifscCode: '', branch: ''
    });

    const [existingUrls, setExistingUrls] = useState({});
    const [files, setFiles] = useState({ photo: null, aadhaar: null, idProof: null, passbook: null });

    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 8;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const userDoc = await getDoc(doc(db, "users", user.uid));
                if (userDoc.exists()) {
                    const sId = userDoc.data().schoolId;
                    setSchoolId(sId);
                    fetchVendors(sId);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const fetchVendors = async (sId) => {
        try {
            const q = query(collection(db, "vendors"), where("schoolId", "==", sId));
            const snap = await getDocs(q);
            setVendors(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) { console.error(error); }
    };

    const handleFileChange = (e) => {
        setFiles({ ...files, [e.target.name]: e.target.files[0] });
    };

    // Helper to view image (Handles both URL and Local File)
    const handlePreview = (itemType) => {
        if (files[itemType]) {
            // Preview newly selected local file
            setPreviewUrl(URL.createObjectURL(files[itemType]));
            setPreviewOpen(true);
        } else if (existingUrls[itemType]) {
            // Preview existing Firestore URL
            setPreviewUrl(existingUrls[itemType]);
            setPreviewOpen(true);
        }
    };

    const uploadFile = async (file, folder) => {
        if (!file) return null;
        const storageRef = ref(storage, `vendors/${schoolId}/${Date.now()}_${folder}`);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return;
        setSaving(true);
        try {
            const urls = {
                photoUrl: await uploadFile(files.photo, 'photo'),
                aadhaarUrl: await uploadFile(files.aadhaar, 'aadhaar'),
                idProofUrl: await uploadFile(files.idProof, 'idProof'),
                passbookUrl: await uploadFile(files.passbook, 'passbook'),
            };
            const payload = {
                ...formData,
                schoolId,
                updatedAt: serverTimestamp(),
                ...(urls.photoUrl && { photoUrl: urls.photoUrl }),
                ...(urls.aadhaarUrl && { aadhaarUrl: urls.aadhaarUrl }),
                ...(urls.idProofUrl && { idProofUrl: urls.idProofUrl }),
                ...(urls.passbookUrl && { passbookUrl: urls.passbookUrl }),
            };
            if (editId) {
                await updateDoc(doc(db, "vendors", editId), payload);
            } else {
                await addDoc(collection(db, "vendors"), { ...payload, createdAt: serverTimestamp() });
            }
            handleClose();
            fetchVendors(schoolId);
        } catch (error) { alert("Error saving vendor"); }
        setSaving(false);
    };

    const startEdit = (v) => {
        setEditId(v.id);
        setFormData({
            name: v.name || '', companyName: v.companyName || '', mobile: v.mobile || '',
            gst: v.gst || '', address: v.address || '', bankName: v.bankName || '',
            accountName: v.accountName || '', accountNo: v.accountNo || '',
            ifscCode: v.ifscCode || '', branch: v.branch || ''
        });
        setExistingUrls({ photo: v.photoUrl, aadhaar: v.aadhaarUrl, idProof: v.idProofUrl, passbook: v.passbookUrl });
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditId(null);
        setFormData({ name: '', companyName: '', mobile: '', gst: '', address: '', bankName: '', accountName: '', accountNo: '', ifscCode: '', branch: '' });
        setFiles({ photo: null, aadhaar: null, idProof: null, passbook: null });
        setExistingUrls({});
    };

    const filteredVendors = vendors.filter(v => 
        v.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
        v.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const paginatedItems = filteredVendors.slice((page - 1) * itemsPerPage, page * itemsPerPage);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress size={30} /></Box>;

    return (
        <Grid  item xs={12} sx={{ mt: 1 }}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary">Vendor Management</Typography>
                    <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={() => setOpen(true)} sx={{ borderRadius: 2 }}>Add Vendor</Button>
                </Box>

                <TextField
                    size="small" placeholder="Search..." sx={{ mb: 2, width: 300 }}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'gray' }} fontSize="small" /> }}
                />

                <TableContainer>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell><b>#</b></TableCell>
                                <TableCell><b>Vendor/Company</b></TableCell>
                                <TableCell><b>Contact</b></TableCell>
                                <TableCell><b>Bank Details</b></TableCell>
                                <TableCell align="right"><b>Actions</b></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedItems.map((v, i) => (
                                <TableRow key={v.id} hover>
                                    <TableCell>{(page - 1) * itemsPerPage + i + 1}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar 
                                                src={v.photoUrl} 
                                                sx={{ width: 32, height: 32, cursor: 'pointer', border: '1px solid #ddd' }} 
                                                onClick={() => { setPreviewUrl(v.photoUrl); setPreviewOpen(true); }}
                                            />
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">{v.name}</Typography>
                                                <Typography variant="caption" color="textSecondary">{v.companyName}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{v.mobile}</Typography>
                                        <Typography variant="caption">{v.gst || 'No GST'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" display="block"><b>A/C:</b> {v.accountNo || 'N/A'}</Typography>
                                        <Typography variant="caption" display="block"><b>IFSC:</b> {v.ifscCode || 'N/A'}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton color="primary" onClick={() => startEdit(v)} size="small"><EditIcon fontSize="inherit" /></IconButton>
                                        <IconButton color="error" onClick={() => { if(window.confirm("Delete?")) deleteDoc(doc(db, "vendors", v.id)).then(() => fetchVendors(schoolId)) }} size="small"><DeleteIcon fontSize="inherit" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Pagination sx={{ mt: 3, display: 'flex', justifyContent: 'center' }} count={Math.ceil(filteredVendors.length / itemsPerPage)} page={page} onChange={(e, v) => setPage(v)} size="small" color="primary" />
            </Paper>

            {/* Main Entry Dialog */}
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">{editId ? "Update Vendor" : "Add New Vendor"}</Typography>
                    <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <Grid className="row" spacing={2}>
                            <Grid className="col-sm-12 mb-3"><Typography variant="subtitle2" color="secondary">Basic Information</Typography></Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}><TextField fullWidth label="Vendor Name" size="small" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}><TextField fullWidth label="Company Name" size="small" value={formData.companyName} onChange={(e) => setFormData({...formData, companyName: e.target.value})} /></Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}><TextField fullWidth label="Mobile" size="small" value={formData.mobile} onChange={(e) => setFormData({...formData, mobile: e.target.value})} /></Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}><TextField fullWidth label="GST" size="small" value={formData.gst} onChange={(e) => setFormData({...formData, gst: e.target.value})} /></Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={4}><TextField fullWidth label="Address" size="small" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} /></Grid>

                            <Grid className="col-sm-12 mb-3" item xs={12} sx={{ mt: 1 }}><Divider sx={{ mb: 1 }} /><Typography variant="subtitle2" color="secondary">Bank Details</Typography></Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}><TextField fullWidth label="Bank Name" size="small" value={formData.bankName} onChange={(e) => setFormData({...formData, bankName: e.target.value})} /></Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}><TextField fullWidth label="Account Holder" size="small" value={formData.accountName} onChange={(e) => setFormData({...formData, accountName: e.target.value})} /></Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={6}><TextField fullWidth label="Account No" size="small" value={formData.accountNo} onChange={(e) => setFormData({...formData, accountNo: e.target.value})} /></Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={3}><TextField fullWidth label="IFSC" size="small" value={formData.ifscCode} onChange={(e) => setFormData({...formData, ifscCode: e.target.value})} /></Grid>
                            <Grid className="col-sm-4 mb-3" item xs={12} sm={3}><TextField fullWidth label="Branch" size="small" value={formData.branch} onChange={(e) => setFormData({...formData, branch: e.target.value})} /></Grid>

                            <Grid  className="col-sm-12 mb-3" item xs={12} sx={{ mt: 1 }}><Divider sx={{ mb: 1 }} /><Typography variant="subtitle2" color="secondary">Attachments</Typography></Grid>
                            {['photo', 'aadhaar', 'idProof', 'passbook'].map((item) => (
                                <Grid className="col-sm-4 mb-3" item xs={12} sm={6} key={item}>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button variant="outlined" component="label" fullWidth size="small" startIcon={files[item] ? <CheckCircleIcon color="success" /> : <UploadIcon />}>
                                            <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>{files[item] ? `${item} Ready` : `Upload ${item}`}</Typography>
                                            <input type="file" hidden name={item} accept="image/*" onChange={handleFileChange} />
                                        </Button>
                                        {(existingUrls[item] || files[item]) && (
                                            <Tooltip title="Preview">
                                                <IconButton size="small" color="primary" onClick={() => handlePreview(item)}>
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleClose} color="inherit">Cancel</Button>
                        <Button type="submit" variant="contained" disabled={saving} color="secondary">
                            {saving ? <CircularProgress size={24} /> : "Save Vendor"}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* --- IMAGE PREVIEW POPUP --- */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm">
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Preview Document
                    <IconButton onClick={() => setPreviewOpen(false)}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 1, textAlign: 'center', bgcolor: '#f0f0f0' }}>
                    <img src={previewUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '4px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }} />
                </DialogContent>
            </Dialog>
        </Grid>
    );
};

export default VendorMgmt;